import { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import SignaturePad from "react-signature-canvas";
import { tokens as t } from "../../theme/tokens";

export const SIGNATURE_EXPORT_WIDTH = 512;
export const SIGNATURE_EXPORT_HEIGHT = 200;

/**
 * The PDF worker inserts the PNG at its natural dimensions, so keep the
 * historical 512 x 200 output even when the responsive drawing pad is smaller
 * or rendered on a high-DPI display.
 */
export const exportSignaturePng = (sourceCanvas) => {
  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = SIGNATURE_EXPORT_WIDTH;
  outputCanvas.height = SIGNATURE_EXPORT_HEIGHT;

  const context = outputCanvas.getContext("2d");
  if (!context) return sourceCanvas.toDataURL("image/png");

  context.clearRect(0, 0, SIGNATURE_EXPORT_WIDTH, SIGNATURE_EXPORT_HEIGHT);
  context.drawImage(
    sourceCanvas,
    0,
    0,
    SIGNATURE_EXPORT_WIDTH,
    SIGNATURE_EXPORT_HEIGHT
  );
  return outputCanvas.toDataURL("image/png");
};

const ESign = ({ saveSignature, isLoading }) => {
  const [emptyError, setEmptyError] = useState(false);
  const sigCanvas = useRef();

  const save = () => {
    if (sigCanvas.current.isEmpty()) {
      setEmptyError(true);
      return;
    }
    setEmptyError(false);
    saveSignature(exportSignaturePng(sigCanvas.current.getCanvas()));
  };

  const clear = () => {
    sigCanvas.current.clear();
    setEmptyError(false);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Draw your signature in the box below.
      </Typography>
      <Box
        sx={{
          width: "100%",
          maxWidth: `${SIGNATURE_EXPORT_WIDTH}px`,
          aspectRatio: `${SIGNATURE_EXPORT_WIDTH} / ${SIGNATURE_EXPORT_HEIGHT}`,
          mx: "auto",
          overflow: "hidden",
          border: `1px solid ${emptyError ? t.color.bad : t.color.inputLine}`,
          borderRadius: `${t.radius.control}px`,
          bgcolor: t.color.paper,
          "& canvas": {
            display: "block",
            width: "100% !important",
            height: "100% !important",
          },
        }}
      >
        <SignaturePad
          ref={sigCanvas}
          canvasProps={{ "aria-label": "Signature drawing area" }}
        />
      </Box>
      {emptyError && (
        <Typography variant="body2" role="alert" sx={{ color: t.color.bad, mt: 0.75 }}>
          Draw your signature before saving.
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        I certify that the information I provided is true and correct. I understand that this electronic consent is treated the same as my written signature on paper, and that I may print these forms as an alternative or in addition to this process.
      </Typography>
      <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2.5 }}>
        <Button variant="outlined" onClick={clear} disabled={isLoading}>Clear</Button>
        <Button variant="contained" onClick={save} disabled={isLoading} startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : undefined}>
          Save signature
        </Button>
      </Stack>
    </Box>
  );
};

export default ESign;
